import React, { FC, useState, useEffect } from 'react'
import { I18n, Auth } from 'aws-amplify'
import {
  makeStyles,
  Theme,
  Grid,
  TextField,
  Link,
  Typography,
  FormControl,
  FormGroup,
  FormControlLabel,
  InputLabel,
  Checkbox
} from '@material-ui/core'

import { PillButton, PasswordRequirementsTooltip } from 'components'
import { AuthFlowSteps, IUser, IOption } from 'types'
import { graphQLQuery, graphQLMutation } from 'graphql/helpers'
import { updateUser } from 'graphql/mutations'
import { userByEmail } from 'graphql/queries'

interface InterestsProps {
  userEmail: string
  setAuthState: (state: AuthFlowSteps) => void
}

const interestOptions: string[] = [
  'manufacturing',
  'healthcare',
  'venues',
  'transport',
  '5gInGermany',
  '5gInFrance',
  '5gInEmea'
]

export const Interests: FC<InterestsProps> = ({ setAuthState, userEmail }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState<boolean>(false)
  const [availableInterests, setAvailableInterests] = useState<string[]>(interestOptions)
  const [userInterests, setUserInterests] = useState<Set<string>>(new Set())

  useEffect(() => {
    return () => setUserInterests(new Set())
  }, [])

  const getCurrentUser = async () => {
    const foundUser = await graphQLQuery(userByEmail, 'userByEmail', { email: userEmail })
    if (!foundUser) {
      setAuthState(AuthFlowSteps.Register)
    }
    return foundUser
  }

  const toggleInterest = (interestName: string) => {
    if (userInterests.has(interestName)) {
      userInterests.delete(interestName)
      setUserInterests(userInterests)
    } else {
      userInterests.add(interestName)
      setUserInterests(userInterests)
    }
  }

  const saveInterests = async () => {
    setLoading(true)
    try {
      const userRes = await getCurrentUser()
      await graphQLMutation(updateUser, {
        id: userRes.id,
        interests: Array.from(userInterests)
      })
      setAuthState(AuthFlowSteps.CommPreference)
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Grid container direction='column' justify='center' spacing={2}>
      <Grid item>
        <Typography variant='h2' className={classes.heading} paragraph>
          <span dangerouslySetInnerHTML={{ __html: I18n.get('interestsTitle') }}></span>
        </Typography>
      </Grid>
      <Grid item container alignItems='center'>
        <Typography component='p' paragraph>
          <span dangerouslySetInnerHTML={{ __html: I18n.get('interestsBlurb') }}></span>
        </Typography>
      </Grid>
      <Grid item container spacing={2} justify='center'>
        <Grid item xs={12}>
          <FormControl fullWidth variant='outlined' className={classes.input}>
            <FormGroup aria-label='position'>
              {availableInterests.map((interestName: string, index: number) => (
                <FormControlLabel
                  key={interestName}
                  value={interestName}
                  control={
                    <Checkbox
                      color='primary'
                      onChange={(e: React.ChangeEvent<{ value: unknown }>) => toggleInterest(e.target.value as string)}
                    />
                  }
                  label={I18n.get(`interestName-${interestName}`)}
                  labelPlacement='end'
                />
              ))}
            </FormGroup>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container item xs={12}>
        <PillButton loading={loading} type='submit' onClick={() => saveInterests()} solid className={classes.button}>
          {I18n.get('next')}
        </PillButton>
      </Grid>
    </Grid>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  heading: {
    fontWeight: 700,
    fontSize: '3.125rem',
    lineHeight: '3.125rem',
    marginBottom: '2rem',
    [theme.breakpoints.down('md')]: {
      fontSize: '3rem',
      lineHeight: '3rem'
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: '2rem'
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '2rem',
      lineHeight: '2rem'
    }
  },
  button: {
    width: 165,
    marginRight: '20px',
    marginBottom: '20px'
  },
  input: {
    color: '#000',
    borderRadius: 0,
    '& .MuiInputBase-root': {
      backgroundColor: '#fff'
    },
    '& label': {
      color: '#000'
    },
    '& fieldset': {
      borderRadius: 0,
      borderColor: '#dadada',
      borderBottomColor: '#000'
    }
  },
  inlineButton: {
    color: '#000',
    margin: '0 .5rem',
    fontFamily: 'Verizon-Regular',
    textDecoration: 'underline',
    verticalAlign: 'initial'
  },
  arrow: {
    color: '#000'
  },
  tooltip: {
    backgroundColor: '#fff',
    border: '1px solid #000'
  },
  tooltipContainer: {
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column'
  },
  tooltipItem: {
    color: '#000',
    display: 'flex',
    alignItems: 'center'
  },

  tooltipIcon: {
    color: theme.palette.grey[500],
    '&:hover': {
      cursor: 'pointer'
    }
  }
}))
